import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usersApi, customRolesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, Trash, UserCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const Users = () => {
  const { t, language } = useLanguage();
  const [users, setUsers] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });

  const roles = [
    { value: 'admin', label: t('admin') },
    { value: 'director_general', label: t('director_general') },
    { value: 'comptable', label: t('comptable') },
    { value: 'rh', label: t('rh') },
    { value: 'director_studies', label: t('director_studies') },
    { value: 'cashier', label: t('cashier') },
    { value: 'secretary', label: t('secretary') },
    { value: 'vigil', label: t('vigil') },
    { value: 'user', label: t('user') },
    ...customRoles.map(c => ({ value: c.code, label: (language === 'fr' ? c.label_fr : (c.label_en || c.label_fr)) })),
  ];

  const roleLabel = (role) => {
    const c = customRoles.find(x => x.code === role);
    if (c) return language === 'fr' ? c.label_fr : (c.label_en || c.label_fr);
    return t(role);
  };

  useEffect(() => {
    loadUsers();
    customRolesApi.list().then(({ data }) => setCustomRoles(data?.custom || [])).catch(() => {});
  }, []);

  const loadUsers = async () => {
    try {
      const { data } = await usersApi.getAll();
      setUsers(data);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await usersApi.create(formData);
      toast.success(t('success'));
      setDialogOpen(false);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDelete = async () => {
    try {
      await usersApi.delete(selectedUser.id);
      toast.success(t('success'));
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'user'
    });
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-700',
      director_general: 'bg-indigo-100 text-indigo-700',
      comptable: 'bg-blue-100 text-blue-700',
      rh: 'bg-green-100 text-green-700',
      director_studies: 'bg-yellow-100 text-yellow-700',
      cashier: 'bg-orange-100 text-orange-700',
      secretary: 'bg-pink-100 text-pink-700',
      vigil: 'bg-slate-200 text-slate-700',
      user: 'bg-gray-100 text-gray-700'
    };
    return colors[role] || colors.user;
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">{t('users')}</h1>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-[#0052FF] hover:bg-[#0043D1]"
          data-testid="add-user-btn"
        >
          <Plus size={18} className="mr-2" />
          {t('addUser')}
        </Button>
      </div>

      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8F0]">
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{language === 'fr' ? 'Nom' : 'Name'}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('email')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('role')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A] text-right">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0052FF] mx-auto" />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} className="border-[#E2E8F0] hover:bg-[#F1F5F9]" data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-manrope">
                      <div className="flex items-center gap-2">
                        <UserCircle size={24} className="text-[#64748B]" />
                        {user.name}
                      </div>
                    </TableCell>
                    <TableCell className="font-manrope">{user.email}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                        {roleLabel(user.role)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSelectedUser(user); setDeleteDialogOpen(true); }}
                        className="hover:bg-[#FEF2F2] text-[#EF4444]"
                        disabled={user.role === 'admin'}
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash size={18} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">{t('addUser')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="font-manrope">{language === 'fr' ? 'Nom' : 'Name'}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="border-[#E2E8F0]"
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('email')}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="border-[#E2E8F0]"
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('password')}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="border-[#E2E8F0]"
                data-testid="user-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('role')}</Label>
              <Select 
                value={formData.role} 
                onValueChange={(v) => setFormData({ ...formData, role: v })}
              >
                <SelectTrigger className="border-[#E2E8F0]" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
              <Button type="submit" className="bg-[#0052FF] hover:bg-[#0043D1]" data-testid="save-user-btn">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-outfit">{t('confirm')}</AlertDialogTitle>
            <AlertDialogDescription className="font-manrope">{t('confirmDelete')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#EF4444] hover:bg-red-600">{t('delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Users;
